import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  addAccessUser,
  changeAccessRole,
  getAccessUsers,
  queryKeys,
  removeAccessUser,
  searchDirectory,
} from '@/api/fpa'
import type { AccessRole, AccessUser, AddUserInput } from '@/api/types'

/** Directory search needs at least this many characters (the API answers `items: []` below it). */
export const DIRECTORY_MIN_QUERY = 2

export function useAccessUsers(enabled = true) {
  return useQuery({
    queryKey: queryKeys.access,
    queryFn: ({ signal }) => getAccessUsers(signal),
    enabled,
    staleTime: 30_000,
  })
}

/** identity's person directory. `term` should already be debounced. */
export function useDirectorySearch(term: string) {
  return useQuery({
    queryKey: queryKeys.directory(term),
    queryFn: ({ signal }) => searchDirectory(term, signal),
    enabled: term.length >= DIRECTORY_MIN_QUERY,
    staleTime: 60_000,
  })
}

type Snapshot = { previous: AccessUser[] | undefined }

/** Optimistic list updates: apply `change`, roll back on error, refetch when settled. */
function useOptimisticAccess<TInput, TResult>(
  mutationFn: (input: TInput) => Promise<TResult>,
  change: (users: AccessUser[], input: TInput) => AccessUser[],
) {
  const queryClient = useQueryClient()
  return useMutation<TResult, unknown, TInput, Snapshot>({
    mutationFn,
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.access })
      const previous = queryClient.getQueryData<AccessUser[]>(queryKeys.access)
      if (previous) queryClient.setQueryData(queryKeys.access, change(previous, input))
      return { previous }
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(queryKeys.access, context.previous)
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['access'] }),
  })
}

export function useAddUser() {
  return useOptimisticAccess(addAccessUser, (users, input: AddUserInput) => {
    const email = input.email.trim().toLowerCase()
    const now = new Date().toISOString()
    const pending: AccessUser = {
      id: `pending:${email}`,
      email,
      name: input.name.trim(),
      role: input.role,
      status: 'active',
      created_by: '',
      created_at: now,
      updated_at: now,
    }
    return [pending, ...users.filter((u) => u.email !== email)]
  })
}

export function useChangeRole() {
  return useOptimisticAccess(
    ({ id, role }: { id: string; role: AccessRole }) => changeAccessRole(id, role),
    (users, { id, role }) => users.map((u) => (u.id === id ? { ...u, role } : u)),
  )
}

export function useRemoveUser() {
  return useOptimisticAccess(removeAccessUser, (users, id: string) =>
    users.map((u) => (u.id === id ? { ...u, status: 'disabled' as const } : u)),
  )
}
